import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import marketplaceService from '../services/marketplaceService';
import './Marketplace.css';

function Marketplace() {
  const navigate = useNavigate();
  
  const [courses, setCourses] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [topRated, setTopRated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    difficulty: '',
    price: '',
    sortBy: 'createdAt'
  });
  const [stats, setStats] = useState(null);

  const loadMarketplace = useCallback(async () => {
    setLoading(true);
    try {
      const [coursesData, featuredData, topRatedData, statsData] = await Promise.all([
        marketplaceService.getAllCourses(filters),
        marketplaceService.getFeaturedCourses(6),
        marketplaceService.getTopRatedCourses(6),
        marketplaceService.getMarketplaceStats()
      ]);
      
      setCourses(coursesData.courses || []);
      setFeatured(featuredData);
      setTopRated(topRatedData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading marketplace:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadMarketplace();
      return;
    }
    
    setLoading(true);
    try {
      const results = await marketplaceService.searchCourses(searchTerm);
      setCourses(results);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value === prev[key] ? '' : value
    }));
  };

  return (
    <div className="marketplace">
      {/* Hero Section */}
      <div className="marketplace-hero">
        <h1>🎓 Course Marketplace</h1>
        <p>Discover courses from expert instructors worldwide</p>
        
        {stats && (
          <div className="marketplace-stats">
            <div className="stat">
              <span className="stat-number">{stats.totalCourses}+</span>
              <span className="stat-label">Courses</span>
            </div>
            <div className="stat">
              <span className="stat-number">{stats.totalInstructors}+</span>
              <span className="stat-label">Instructors</span>
            </div>
            <div className="stat">
              <span className="stat-number">{stats.totalEnrollments}+</span>
              <span className="stat-label">Students</span>
            </div>
          </div>
        )}
        
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search for courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch}>
            🔍 Search
          </button>
        </div>
      </div>

      {/* Featured Courses */}
      {featured.length > 0 && (
        <section className="marketplace-section">
          <h2>🔥 Featured Courses</h2>
          <div className="courses-grid">
            {featured.map(course => (
              <CourseCard key={course.id} course={course} navigate={navigate} />
            ))}
          </div>
        </section>
      )}

      {/* Top Rated */}
      {topRated.length > 0 && (
        <section className="marketplace-section">
          <h2>⭐ Top Rated Courses</h2>
          <div className="courses-grid">
            {topRated.map(course => (
              <CourseCard key={course.id} course={course} navigate={navigate} />
            ))}
          </div>
        </section>
      )}

      {/* Filters */}
      <div className="marketplace-filters">
        <h3>Filter Courses</h3>
        
        <div className="filter-group">
          <label>Difficulty:</label>
          <div className="filter-buttons">
            <button 
              className={filters.difficulty === 'beginner' ? 'active' : ''}
              onClick={() => handleFilterChange('difficulty', 'beginner')}
            >
              Beginner
            </button>
            <button 
              className={filters.difficulty === 'intermediate' ? 'active' : ''}
              onClick={() => handleFilterChange('difficulty', 'intermediate')}
            >
              Intermediate
            </button>
            <button 
              className={filters.difficulty === 'advanced' ? 'active' : ''}
              onClick={() => handleFilterChange('difficulty', 'advanced')}
            >
              Advanced
            </button>
          </div>
        </div>
        
        <div className="filter-group">
          <label>Price:</label>
          <div className="filter-buttons">
            <button 
              className={filters.price === 'free' ? 'active' : ''}
              onClick={() => handleFilterChange('price', 'free')}
            >
              Free
            </button>
            <button 
              className={filters.price === 'paid' ? 'active' : ''}
              onClick={() => handleFilterChange('price', 'paid')}
            >
              Paid
            </button>
          </div>
        </div>
        
        <div className="filter-group">
          <label>Sort By:</label>
          <select 
            value={filters.sortBy}
            onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
          >
            <option value="createdAt">Newest</option>
            <option value="enrollmentCount">Most Popular</option>
            <option value="averageRating">Highest Rated</option>
            <option value="price">Price: Low to High</option>
          </select>
        </div>
      </div>

      {/* All Courses */}
      <section className="marketplace-section">
        <h2>📚 All Courses</h2>
        
        {loading ? (
          <div className="loading">Loading courses...</div>
        ) : courses.length === 0 ? (
          <div className="no-courses">
            <p>No courses found matching your criteria.</p>
            <button onClick={loadMarketplace}>Clear Filters</button>
          </div>
        ) : (
          <div className="courses-grid">
            {courses.map(course => (
              <CourseCard key={course.id} course={course} navigate={navigate} />
            ))}
          </div>
        )}
      </section>

      {/* CTA Section */}
      <div className="marketplace-cta">
        <h2>Want to Teach?</h2>
        <p>Share your expertise and earn money as an instructor!</p>
        <button onClick={() => navigate('/become-instructor')}>
          🎓 Become an Instructor
        </button>
      </div>
    </div>
  );
}

// Course Card Component
function CourseCard({ course, navigate }) {
  return (
    <div 
      className="course-card"
      onClick={() => navigate(`/course/${course.id}`)}
    >
      {course.thumbnail && (
        <div className="course-thumbnail">
          <img src={course.thumbnail} alt={course.title} />
        </div>
      )}
      
      <div className="course-info">
        <div className="course-header">
          <h3>{course.title}</h3>
          {course.price === 0 ? (
            <span className="price free">FREE</span>
          ) : (
            <span className="price">${course.price}</span>
          )}
        </div>
        
        <p className="course-description">
          {course.description?.substring(0, 100)}...
        </p>
        
        {course.instructor && (
          <div className="course-instructor">
            <span className="instructor-avatar">
              {course.instructor.profileImage ? (
                <img src={course.instructor.profileImage} alt={course.instructor.name} />
              ) : (
                '👨‍🏫'
              )}
            </span>
            <span className="instructor-name">{course.instructor.name}</span>
            {course.instructor.isVerified && <span className="verified">✓</span>}
          </div>
        )}
        
        <div className="course-stats">
          <span>👥 {course.enrollmentCount || 0} students</span>
          {course.averageRating > 0 && (
            <span>⭐ {course.averageRating.toFixed(1)} ({course.reviewCount})</span>
          )}
        </div>
        
        {course.difficulty && (
          <span className={`difficulty-badge ${course.difficulty}`}>
            {course.difficulty}
          </span>
        )}
      </div>
    </div>
  );
}

export default Marketplace;
